import { SEARCH_PARAMETERS_SCHEMA } from './data';
import {
  isArrayKind,
  SearchParameterFieldKind,
  type SearchParameterSchemaRow,
} from './rows';
import type { SchemaDefaults } from './derived';

/**
 * Indexes a schema by parameter name so lookups are O(1).
 *
 * Takes the schema as an argument rather than closing over
 * SEARCH_PARAMETERS_SCHEMA so callers that accept an injected schema
 * (SearchParameterDeserializer) can share this implementation.
 */
export const schemaByName = (
  schema: readonly SearchParameterSchemaRow[]
): ReadonlyMap<string, SearchParameterSchemaRow> =>
  new Map(schema.map((row) => [row.name, row]));

const NO_VALUES: readonly never[] = Object.freeze([]);

/**
 * The value every parameter falls back to when it is absent or cleared.
 *
 * An array parameter falls back to an empty list, and says so here rather than
 * in each array row: holding none of something is what an array parameter
 * means when it is unstated, so it is a property of the kind and not a choice
 * a row makes. A string parameter is the opposite — absent and empty are
 * different answers — which is why only array kinds get this.
 *
 * The empty list is shared and frozen rather than built per search. Nothing
 * reads one of these and mutates it, and freezing is what keeps that true: a
 * shared array that could be pushed to would leak one search's values into
 * every other.
 *
 * Only ENUM and NUMBER rows can carry a `defaultValue` — see the
 * SearchParameterSchemaRow union in ./rows — so that check is exhaustive
 * rather than a narrowing convenience.
 */
export const schemaDefaults = (
  schema: readonly SearchParameterSchemaRow[]
): Readonly<Record<string, unknown>> => {
  const defaults: Record<string, unknown> = {};
  for (const row of schema) {
    if (isArrayKind(row.kind)) {
      defaults[row.name] = NO_VALUES;
    } else if (
      (row.kind === SearchParameterFieldKind.ENUM ||
        row.kind === SearchParameterFieldKind.NUMBER) &&
      'defaultValue' in row
    ) {
      defaults[row.name] = row.defaultValue;
    }
  }
  return defaults;
};

export const SCHEMA_BY_NAME = schemaByName(SEARCH_PARAMETERS_SCHEMA);

// The one place the derived shape is asserted onto the computed object: the
// loop above cannot carry the name/value pairing through, while the rows it
// reads do.
export const SCHEMA_DEFAULTS = schemaDefaults(
  SEARCH_PARAMETERS_SCHEMA
) as SchemaDefaults;

/** The kind of the named parameter, or a throw if the schema has no such name. */
export const kindOf = (name: string): SearchParameterFieldKind => {
  const row = SCHEMA_BY_NAME.get(name);
  if (!row) {
    throw new Error(`Unknown search parameter: ${name}`);
  }
  return row.kind;
};
